import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { NavLink } from 'react-router';
import type { Freshness } from '../lib/format';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-on-accent hover:bg-accent-strong border-transparent',
  secondary: 'bg-surface text-ink border-rule hover:border-muted',
  ghost: 'bg-transparent text-muted border-transparent hover:text-ink hover:bg-sunken',
  danger: 'bg-surface text-critical border-rule hover:border-critical',
};

export function Button({ variant = 'secondary', className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        className,
      )}
    />
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx('rounded-lg border border-rule bg-surface', className)}>{children}</div>;
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <div className="mt-1 text-sm text-muted">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Section({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-lg border border-dashed border-rule px-4 py-8 text-center text-sm text-muted">{children}</div>;
}

export function Loading() {
  return <div className="py-10 text-center text-sm text-muted">Loading…</div>;
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const raw = error instanceof Error ? error.message : 'Something went wrong.';
  // Database messages start in lower case ("requires an admin of this organization").
  const message = raw.charAt(0).toUpperCase() + raw.slice(1);
  return <div className="rounded-md border border-critical/40 bg-critical/10 px-3 py-2 text-sm text-critical">{message}</div>;
}

const SEVERITY_CLASS: Record<string, string> = {
  critical: 'bg-critical/15 text-critical',
  high: 'bg-high/15 text-high',
  medium: 'bg-medium/15 text-medium',
  low: 'bg-low/15 text-low',
};

export function SeverityBadge({ severity }: { severity: string | null }) {
  const s = severity ?? 'info';
  return <span className={cx('inline-block rounded px-1.5 py-0.5 text-xs font-medium capitalize', SEVERITY_CLASS[s] ?? 'bg-sunken text-muted')}>{s}</span>;
}

const FRESHNESS_DOT: Record<Freshness, string> = {
  live: 'bg-accent',
  delayed: 'bg-medium',
  offline: 'bg-critical',
  never: 'bg-rule',
};

export function FreshnessBadge({ state, label, detail }: { state: Freshness; label: string; detail?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm" title={detail}>
      <span className={cx('h-2 w-2 rounded-full', FRESHNESS_DOT[state], state === 'live' && 'animate-pulse')} />
      <span className="font-medium">{label}</span>
      {detail && <span className="text-muted">{detail}</span>}
    </span>
  );
}

export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

/** A value to copy: a command, a link, a code. */
export function CopyField({ value, label }: { value: string; label?: string }) {
  return (
    <div className="flex items-stretch gap-2">
      <code className="tabular min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-rule bg-sunken px-3 py-2 text-sm" aria-label={label}>
        {value}
      </code>
      <CopyButton text={value} />
    </div>
  );
}

/** A few lines of code to copy, e.g. a script example. */
export function CodeBlock({ code }: { code: string }) {
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md border border-rule bg-sunken px-3 py-2 pr-20 text-sm"><code>{code}</code></pre>
      <div className="absolute right-2 top-2">
        <CopyButton text={code} />
      </div>
    </div>
  );
}

export function Dialog({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // showModal() focuses the first focusable element (the close button); start in the form.
      d.querySelector<HTMLElement>('input, select, textarea')?.focus();
    }
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className="m-auto w-[min(36rem,calc(100vw-2rem))] rounded-lg border border-rule bg-surface p-0 text-ink backdrop:bg-black/40"
    >
      <div className="flex items-center justify-between border-b border-rule px-5 py-3">
        <h2 className="text-base font-semibold">{title}</h2>
        <button type="button" onClick={onClose} className="text-muted hover:text-ink" aria-label="Close">
          ✕
        </button>
      </div>
      <div className="px-5 py-4">{open && children}</div>
    </dialog>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    </label>
  );
}

export const inputClass =
  'w-full rounded-md border border-rule bg-bg px-3 py-1.5 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none';

export function Tabs({ items }: { items: { to: string; label: string; end?: boolean }[] }) {
  return (
    <nav className="mb-6 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-rule">
      {items.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            cx('-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium', isActive ? 'border-accent text-ink' : 'border-transparent text-muted hover:text-ink')
          }
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function Logo({ className = 'h-7 w-7' }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <rect width="64" height="64" rx="14" fill="var(--ink)" />
      <g transform="translate(11.86 47.41) scale(0.06021)">
        <path d="M171 0 5 -525H158L263 -120H267L375 -525H520L354 0Z" fill="var(--bg)" />
        <path d="M571 13Q525 13 502 -7Q479 -27 479 -69Q479 -111 502 -131Q525 -151 571 -151Q617 -151 640.5 -131Q664 -111 664 -69Q664 13 571 13Z" fill="#6DB58E" />
      </g>
    </svg>
  );
}

export { cx };
